import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const dropbox = defineConnector({
  identifier: "dropbox",
  name: "Dropbox",
  tagLine: "Cloud file storage and sharing",
  keywords: "cloud storage files folders sharing documents",
  logo,
  protocol: "http",
  tags: ["documents", "data"],
  defaultUrlPattern: () =>
    "https://api\\.dropboxapi\\.com/.*|https://content\\.dropboxapi\\.com/.*",
  auth: oauthAuth,
  steps: [
    {
      title: "Create a Dropbox app",
      items: [
        {
          kind: "markdown",
          content:
            "Visit the [Dropbox App Console](https://www.dropbox.com/developers/apps) and click **'Create app'**.\n\nSelect **'Scoped access'**, choose **'Full Dropbox'** or **'App folder'** depending on your needs, and give your app a name.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content:
            "In your app's settings, go to the **'OAuth 2'** section and add the following redirect URI:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/dropbox/callback",
        },
      ],
    },
    {
      title: "Copy your app credentials",
      items: [
        {
          kind: "markdown",
          content:
            "From your app's settings page, copy the **App key** and **App secret**.",
        },
        {
          kind: "input",
          label: "App key",
          placeholder: "Paste your App key",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "App secret",
          placeholder: "Paste your App secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Configure permissions",
      items: [
        {
          kind: "markdown",
          content:
            "In your app's **'Permissions'** tab, enable the scopes you need (e.g., `files.configuration.read`, `files.content.read`, `files.content.write`).\n\nThen enter the scopes below as a space-separated list:",
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "files.configuration.read files.content.read",
          helperText: "Space-separated list of permission scopes.",
          target: { kind: "oauthConnection", field: "scopes" },
        },
      ],
    },
    {
      title: "Authorize your Dropbox account",
      items: [
        {
          kind: "markdown",
          content: "Click below to authorize your Dropbox account.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () => "https://www.dropbox.com/oauth2/authorize",
    tokenEndpoint: () => "https://api.dropboxapi.com/oauth2/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => [],
    authorizationParameters: () => ({
      token_access_type: "offline",
    }),
  },
  connectionTest: {
    protocol: "http",
    request: () => "https://api.dropboxapi.com/2/users/get_current_account",
  },
});
